// Read the original email and current contact eligibility in one transaction.
// A retry always uses the saved addresses and attachments. The live contact
// book controls eligibility only; editing a contact cannot redirect a retry.
import { doc, runTransaction } from 'firebase/firestore';
import { db } from './firebase';

const TERMINAL_ATTEMPTS = new Set(['delivered', 'warning', 'partial_success', 'failed']);

export async function loadEmailRetryDetail(companyId, emailId, expected) {
  if (!companyId || !emailId) throw new Error('This output has no email to retry.');
  const root = `users/${companyId}/emails/${emailId}`;

  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(doc(db, root));
    if (!snapshot.exists()) throw new Error('The original email record is no longer available.');
    const email = snapshot.data();
    const source = email.source;
    if (
      email.email_id !== emailId
      || email.source_type !== 'function'
      || source?.function_id !== expected.functionId
      || source?.run_id !== expected.runId
      || source?.output_block_id !== expected.outputBlockId
    ) {
      throw new Error('The stored email does not belong to this run and output.');
    }
    if (!email.current_attempt_id) throw new Error('The original email has no recorded attempt.');
    const attemptSnapshot = await transaction.get(doc(db, `${root}/attempts/${email.current_attempt_id}`));
    if (!attemptSnapshot.exists()) throw new Error('The original email attempt is no longer available.');
    const attempt = attemptSnapshot.data();
    if (attempt.attempt_id !== email.current_attempt_id) {
      throw new Error('The original email attempt identity does not match.');
    }
    const attemptComplete = TERMINAL_ATTEMPTS.has(attempt.status);
    const outcomes = new Map(email.recipient_outcomes.map((outcome) => [outcome.contact_id, outcome]));
    const recipients = [];
    for (const recipient of email.recipients) {
      const outcome = outcomes.get(recipient.contact_id);
      if (!outcome) throw new Error('An original recipient has no recorded delivery.');
      const deliverySnapshot = await transaction.get(doc(db, `${root}/deliveries/${outcome.latest_delivery_id}`));
      if (!deliverySnapshot.exists()) throw new Error('An original recipient delivery is no longer available.');
      const delivery = deliverySnapshot.data();
      if (
        delivery.delivery_id !== outcome.latest_delivery_id
        || delivery.attempt_id !== outcome.latest_attempt_id
        || delivery.recipient?.contact_id !== recipient.contact_id
        || delivery.status !== outcome.status
      ) {
        throw new Error('An original recipient delivery does not match the email.');
      }
      const contactSnapshot = await transaction.get(doc(db, `users/${companyId}/contacts/${recipient.contact_id}`));
      const contact = contactSnapshot.exists() ? contactSnapshot.data() : null;
      let unavailableReason = null;
      if (!attemptComplete) unavailableReason = 'The current email attempt is still in progress.';
      else if (!delivery.retry_eligible) unavailableReason = 'This delivery cannot be safely retried.';
      else if (!contact) unavailableReason = 'This contact no longer exists.';
      else if (contact.status !== 'active') unavailableReason = `This contact is ${contact.status}. Restore it in Settings to retry.`;
      else if (contact.provider_suppression_cleanup && contact.provider_suppression_cleanup.status !== 'complete') {
        unavailableReason = 'Provider suppression cleanup has not completed.';
      }
      recipients.push({
        contactId: recipient.contact_id,
        displayName: recipient.display_name,
        address: recipient.email,
        status: delivery.status,
        retryEligible: unavailableReason === null,
        unavailableReason,
      });
    }
    return {
      emailId,
      finalSubject: email.final_subject,
      attachmentNames: email.attachments.map((attachment) => attachment.filename),
      missingAttachments: email.missing_attachments,
      recipients,
      retryAvailable: recipients.some((recipient) => recipient.retryEligible),
    };
  });
}
